// src/components/common/intercom/IntercomDashboard.js
import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider, 
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
  Grid,
  Container,
  Modal,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  MessageSquare, 
  X, 
  ChevronDown, 
  CheckCircle, 
  HelpCircle,
  Clock,
  Compass,
  Server,
  Cpu,
  ChevronRight,
  ArrowRight,
  MessageCircle,
  Book,
  AlertCircle,
  Settings,
  Calendar,
  Users,
  FileText,
  Shield,
  RefreshCw,
  Zap,
  List as ListIcon,
  MessageSquareText,
  Bell,
  UserCheck
} from 'lucide-react';
import { useIntercom } from './useIntercom';
import { motion, AnimatePresence } from 'framer-motion';

// Colores corporativos
const HEALTH_GREEN = '#4CAF50';
const GREEN_GRADIENT_START = '#4CAF50';
const GREEN_GRADIENT_MIDDLE = '#3D8B40';
const GREEN_GRADIENT_END = '#2E7031';

// Componentes estilizados con la estética del primer código
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '28px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(4),
  overflow: 'hidden',
  backgroundColor: '#ffffff'
}));

const PaperHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const PaperContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4)
}));

const GreenButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${GREEN_GRADIENT_START} 0%, ${GREEN_GRADIENT_MIDDLE} 50%, ${GREEN_GRADIENT_END} 100%)`,
  color: 'white',
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.5, 3),
  fontSize: '1rem',
  boxShadow: '0 4px 6px rgba(76, 175, 80, 0.2)',
  '&:hover': {
    background: `linear-gradient(135deg, ${GREEN_GRADIENT_MIDDLE} 0%, ${GREEN_GRADIENT_END} 100%)`,
    boxShadow: '0 6px 8px rgba(76, 175, 80, 0.3)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.7)',
    background: '#9CA3AF'
  }
}));

const OutlinedGreenButton = styled(Button)(({ theme }) => ({
  color: HEALTH_GREEN,
  borderColor: HEALTH_GREEN,
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.5, 3),
  fontSize: '1rem',
  '&:hover': {
    backgroundColor: 'rgba(76, 175, 80, 0.04)',
    borderColor: HEALTH_GREEN,
    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.1)',
  }
}));

const LogoAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  background: `linear-gradient(135deg, ${GREEN_GRADIENT_START} 0%, ${GREEN_GRADIENT_MIDDLE} 50%, ${GREEN_GRADIENT_END} 100%)`,
  borderRadius: theme.spacing(1.5),
  marginRight: theme.spacing(2)
}));

const StatusChip = styled(Chip)(({ theme, statusType }) => {
  let bgColor, textColor, borderColor;
  
  switch (statusType) {
    case 'connected':
      bgColor = 'rgba(76, 175, 80, 0.1)';
      textColor = '#2e7d32';
      borderColor = 'rgba(76, 175, 80, 0.3)';
      break;
    case 'connecting':
      bgColor = 'rgba(25, 118, 210, 0.1)';
      textColor = '#1565c0';
      borderColor = 'rgba(25, 118, 210, 0.3)';
      break;
    case 'error':
      bgColor = 'rgba(211, 47, 47, 0.1)';
      textColor = '#c62828';
      borderColor = 'rgba(211, 47, 47, 0.3)';
      break;
    default:
      bgColor = 'rgba(158, 158, 158, 0.1)';
      textColor = '#757575';
      borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    backgroundColor: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.spacing(3),
    '& .MuiChip-label': {
      padding: 0,
      paddingLeft: theme.spacing(0.5)
    },
    '& .MuiChip-icon': {
      marginLeft: 0,
      marginRight: theme.spacing(0.5)
    }
  };
});

const InfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)'
  }
}));

const CategoryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(3.5),
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-3px)'
  }
}));

const IconCircle = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2)
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '16px !important',
  border: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '8px 0',
  },
  boxShadow: 'none',
  overflow: 'hidden'
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1, 0),
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2, 3),
}));

const ArticleItem = styled(motion.div)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(3.5),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  }
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
  maxWidth: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(3),
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  padding: theme.spacing(4),
 
  /* Estilo personalizado para scrollbar */
  scrollbarWidth: 'thin', /* para Firefox */
  scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent', /* para Firefox */
 
  /* Para navegadores WebKit (Chrome, Safari, Edge) */
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '10px',
    border: 'none',
  },
  '&::-webkit-scrollbar-button': {
    display: 'none',
  },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
}));

const ModalIconCircle = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}));

// Componente principal
const IntercomDashboard = () => {
  const [intercomReady, setIntercomReady] = useState(false);
  const { showMessenger, hideMessenger, showNewMessage, getUnreadCount, intercomAvailable } = useIntercom();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // FAQ Data
  const faqData = [
    {
      id: 1,
      question: "¿Cómo maneja AGT Health los pagos de consultas?",
      answer: "AGT Health no actúa como intermediario en los pagos. Cada profesional de salud tiene la libertad total para gestionar sus propios cobros y elegir el método de pago que considere conveniente (efectivo, transferencia, etc.). Esto es una propuesta de valor que brinda mayor personalización y flexibilidad, sin interferencias de nuestra parte en precios o metodologías de cobro."
    },
    {
      id: 2,
      question: "¿Qué pasa si se vence mi suscripción?",
      answer: "Si tu suscripción se vence, no podrás seguir accediendo a la aplicación inmediatamente. Esto es necesario porque las funcionalidades como la toma de citas tienen costos operativos asociados que requieren ser cubiertos. Te recomendamos asegurar que tu método de pago tenga fondos suficientes antes del período de facturación."
    },
    {
      id: 3,
      question: "¿El soporte está disponible 24/7?",
      answer: "Sí, nuestro soporte técnico está disponible las 24 horas, los 7 días de la semana. Estamos preparados para resolver problemas técnicos relacionados con la toma de citas online y cualquier inconveniente que pueda surgir. Priorizamos la tranquilidad de nuestros profesionales y solucionamos los problemas sin importar la hora."
    },
    {
      id: 4,
      question: "¿Los pacientes pueden ver mis precios?",
      answer: "Los pacientes pueden ver el costo de tus consultas solo si tú lo decides. Puedes optar por mostrar los precios en tu perfil público para que los pacientes estén informados, o mantenerlos privados. Recomendamos la transparencia en precios, pero respetamos tu decisión. También puedes usar AGT AI para comunicar información de precios cuando sea necesario."
    },
    {
      id: 5,
      question: "¿Qué puede hacer la Inteligencia Artificial de AGT?",
      answer: "La IA de AGT puede agendar pacientes, crear registros de nuevos pacientes, enviar recordatorios automatizados, y comunicarse con pacientes vía WhatsApp (con limitaciones). Puedes instruir a la IA para que responda dudas específicas de los usuarios y automatice tareas administrativas, mejorando la eficiencia de tu práctica médica."
    },
    {
      id: 6,
      question: "¿Cómo puedo visualizar las tendencias de signos vitales de mis pacientes?",
      answer: "Puedes acceder a esta información en la sección 'Registros'. Allí encontrarás gráficos que muestran la evolución de los signos vitales, métricas corporales y valores de laboratorio a lo largo del tiempo. Estos registros combinan tanto los datos tomados durante las consultas como los que el paciente registra en su vida cotidiana."
    },
    {
      id: 7,
      question: "¿Cómo funciona el widget de tareas?",
      answer: "El widget de tareas te permite gestionar todas tus actividades pendientes. Puedes añadir tareas manualmente o utilizar la función 'AGT-Task' en el widget de notas para convertir automáticamente tus anotaciones en tareas estructuradas. Las tareas pueden incluir recordatorios para llamar pacientes, revisar información médica o cualquier otra actividad relacionada con tu práctica."
    },
    {
      id: 8,
      question: "¿Puedo personalizar mi horario de atención?",
      answer: "Sí, en la sección 'Mi AGT' puedes configurar completamente tus horarios de atención. Esto incluye definir días laborables, horas disponibles, duración de las citas y tiempo mínimo de anticipación para reservas. También puedes añadir fechas especiales en las que no estarás disponible, como vacaciones o días festivos."
    },
    {
      id: 9,
      question: "¿Los pacientes pueden ver mi perfil sin que yo lo autorice?",
      answer: "No. Tú tienes control total sobre la visibilidad de tu perfil público. En la sección 'Mi AGT' encontrarás un botón que te permite decidir cuándo publicar o despublicar tu perfil. Solo cuando decidas hacerlo público, los pacientes podrán ver tu información y agendar citas contigo."
    },
    {
      id: 10,
      question: "¿Cómo puedo enviar recordatorios a mis pacientes?",
      answer: "Existen varias formas. Desde el widget 'Próximas Citas' puedes usar el botón de WhatsApp para enviar recordatorios masivos a todos los pacientes con citas programadas. También puedes enviar recordatorios individuales desde la sección 'Citas'. La IA de AGT puede automatizar estos recordatorios si se lo solicitas."
    }
  ];

  // Datos de acceso rápido
  const quickLinks = [
    {
      icon: <MessageCircle size={24} />,
      title: "Chat en Vivo",
      description: "Conéctate con nuestro equipo de soporte al instante",
      available: true
    }
  ];

  // Categorías de soporte con artículos completos
  const supportCategories = [
    {
      id: 'getting-started',
      icon: <Compass size={24} />,
      title: "Primeros Pasos",
      description: "¿Nuevo en AGT Health? Comienza aquí",
      articles: [
        {
          id: 'quickstart',
          title: "Guía de inicio rápido",
          icon: <Zap size={24} />,
          content: {
            intro: "Bienvenido a AGT Health. Esta guía te ayudará a dar tus primeros pasos en la plataforma.",
            sections: [
              {
                title: "Navegación por el Dashboard",
                content: "El Dashboard es tu centro de control. Aquí encontrarás cinco widgets principales: Resumen del Paciente, Próximas Citas, Mis Pacientes, Notas y Tareas. Cada uno funciona como un atajo para acceder rápidamente a las funcionalidades más utilizadas de la plataforma."
              },
              {
                title: "Widgets Principales",
                content: "El widget de Resumen del Paciente muestra información del paciente más reciente, incluyendo signos vitales y datos personales. Próximas Citas te permite ver y gestionar tus citas programadas. Mis Pacientes ofrece acceso a todos los pacientes registrados. En Notas puedes escribir apuntes que se guardan automáticamente. El widget de Tareas te ayuda a organizar tus pendientes."
              },
              {
                title: "Sidebar",
                content: "En la barra lateral encontrarás acceso a todas las secciones: Dashboard, Mi AGT, Pacientes, Citas, Tareas, Registros, Soporte y Configuración. Cada sección te permite gestionar aspectos específicos de tu práctica profesional."
              },
              {
                title: "Chat de AGT",
                content: "El asistente virtual está disponible para ayudarte con cualquier tarea. Puedes pedirle que agende citas, busque información de pacientes, envíe recordatorios y más, todo mediante comandos de texto naturales."
              }
            ],
            conclusion: "Comienza explorando cada sección para familiarizarte con la plataforma. Si tienes dudas, el chat de soporte está disponible 24/7."
          }
        },
        {
          id: 'profile-setup',
          title: "Configuración de tu perfil AGT",
          icon: <UserCheck size={24} />,
          content: {
            intro: "Tu perfil profesional es tu carta de presentación para los pacientes. Aprende a configurarlo correctamente para maximizar tu visibilidad.",
            sections: [
              {
                title: "Acceso a Mi AGT",
                content: "Para configurar tu perfil, dirígete a la sección 'Mi AGT' en el sidebar. Aquí podrás personalizar toda la información que verán tus potenciales pacientes."
              },
              {
                title: "Información Básica",
                content: "Sube una foto profesional, añade tu título, años de experiencia y una descripción atractiva de tu práctica profesional. Esta información es crucial para generar confianza en los pacientes que visiten tu perfil."
              },
              {
                title: "Especialidades y Modalidades",
                content: "Configura tus especialidades médicas y las modalidades de atención que ofreces (presencial, online, domicilio). Esto ayuda a los pacientes a encontrarte cuando buscan profesionales con tu especialidad."
              },
              {
                title: "Horarios y Disponibilidad",
                content: "Define tus días y horarios de atención, duración de las consultas y tiempo mínimo de anticipación para reservas. También puedes bloquear fechas especiales en las que no estarás disponible."
              },
              {
                title: "Publicación del Perfil",
                content: "Una vez configurada toda la información, utiliza el botón de publicación para hacer visible tu perfil a los pacientes. Puedes activar o desactivar esta visibilidad en cualquier momento según tus necesidades."
              }
            ],
            conclusion: "Un perfil bien configurado aumenta significativamente tus posibilidades de captar nuevos pacientes. Dedica tiempo a completar todos los campos y mantén tu información actualizada."
          }
        },
        {
          id: 'patient-management',
          title: "Gestión de pacientes",
          icon: <Users size={24} />,
          content: {
            intro: "Aprende a gestionar eficientemente la información de tus pacientes en AGT Health.",
            sections: [
              {
                title: "Añadir Nuevos Pacientes",
                content: "Puedes agregar nuevos pacientes manualmente desde la sección 'Pacientes' o desde el widget 'Mis Pacientes'. También se crean automáticamente cuando un paciente agenda una cita a través de tu perfil público."
              },
              {
                title: "Búsqueda de Pacientes",
                content: "Utiliza la función de búsqueda para encontrar rápidamente a cualquier paciente registrado. Puedes buscar por nombre, identificación o cualquier otro dato relevante."
              },
              {
                title: "Ficha del Paciente",
                content: "Accede a la ficha completa para ver toda la información del paciente, incluyendo datos personales, historial de citas, signos vitales y registros médicos. Desde aquí también puedes actualizar cualquier información."
              },
              {
                title: "Registros Médicos",
                content: "En la sección 'Registros' puedes ver y actualizar los registros médicos de cada paciente, como peso, estatura, signos vitales y resultados de laboratorio. También puedes visualizar tendencias a lo largo del tiempo."
              }
            ],
            conclusion: "Una gestión eficiente de los pacientes te permite ofrecer una atención más personalizada y de mayor calidad. Mantén sus registros actualizados después de cada consulta."
          }
        },
        {
          id: 'appointment-scheduling',
          title: "Programación de citas",
          icon: <Calendar size={24} />,
          content: {
            intro: "La gestión eficiente de tu agenda es fundamental. Aprende a manejar tus citas con AGT Health.",
            sections: [
              {
                title: "Crear Nuevas Citas",
                content: "Puedes programar citas desde el widget 'Próximas Citas', la sección 'Citas' del sidebar, o pidiéndole a la IA que lo haga por ti. Las citas también pueden ser agendadas directamente por los pacientes a través de tu perfil público."
              },
              {
                title: "Gestión de Citas",
                content: "Visualiza todas tus citas en formato de calendario. Puedes editar detalles como fecha, hora o duración, y también cancelar citas si es necesario. Cada cambio puede ser notificado automáticamente al paciente."
              },
              {
                title: "Recordatorios",
                content: "Envía recordatorios a tus pacientes directamente desde la plataforma. Puedes hacerlo de forma masiva para todas las citas próximas o individualmente para pacientes específicos. Los recordatorios se envían vía WhatsApp."
              },
              {
                title: "Registro de Asistencia",
                content: "Después de cada consulta, registra la asistencia del paciente y actualiza sus datos médicos. Esto te ayudará a mantener un historial preciso y actualizado de cada paciente."
              }
            ],
            conclusion: "Una agenda bien organizada optimiza tu tiempo y mejora la experiencia de tus pacientes. Utiliza todas las herramientas disponibles para gestionar eficientemente tus citas."
          }
        },
        {
          id: 'ai-intro',
          title: "Entendiendo la IA de AGT",
          icon: <Cpu size={24} />,
          content: {
            intro: "El asistente de inteligencia artificial de AGT es una herramienta poderosa que puede ayudarte a optimizar tu práctica profesional.",
            sections: [
              {
                title: "Capacidades Básicas",
                content: "La IA puede agendar citas, crear registros de pacientes, enviar recordatorios, buscar información específica y convertir notas en tareas estructuradas. Está diseñada para entender lenguaje natural, por lo que puedes comunicarte con ella como lo harías con un asistente humano."
              },
              {
                title: "Comandos Útiles",
                content: "Algunos comandos útiles incluyen: 'Agenda una cita para [paciente] el [fecha]', 'Busca información sobre [paciente]', 'Envía un recordatorio a [paciente]', 'Convierte estas notas en tareas', entre otros. La IA aprenderá de tus interacciones para ser más eficiente con el tiempo."
              },
              {
                title: "Automatización de Tareas",
                content: "Una de las funciones más útiles es la conversión de notas en tareas. Escribe tus pendientes en el widget de Notas y luego usa el botón 'AGT-Task' para que la IA las convierta automáticamente en tareas estructuradas y organizadas."
              },
              {
                title: "Comunicación con Pacientes",
                content: "La IA puede ayudarte a comunicarte con tus pacientes vía WhatsApp, enviando recordatorios de citas o mensajes personalizados. Esto automatiza una parte importante de la gestión administrativa de tu consulta."
              }
            ],
            conclusion: "Familiarizarte con las capacidades de la IA te permitirá automatizar tareas repetitivas y enfocarte más en la atención a tus pacientes. Experimenta con diferentes comandos para descubrir todo su potencial."
          }
        }
      ]
    },
    {
      id: 'technical',
      icon: <Server size={24} />,
      title: "Soporte Técnico",
      description: "Documentación técnica y APIs",
      articles: [
        {
          id: 'system-requirements',
          title: "Requisitos del sistema",
          icon: <Settings size={24} />,
          content: {
            intro: "Para garantizar un funcionamiento óptimo de AGT Health, asegúrate de cumplir con los siguientes requisitos técnicos.",
            sections: [
              {
                title: "Navegadores Compatibles",
                content: "AGT Health funciona mejor en las versiones más recientes de Google Chrome, Mozilla Firefox, Safari y Microsoft Edge. Recomendamos mantener tu navegador actualizado para aprovechar todas las funcionalidades y garantizar la seguridad de tus datos."
              },
              {
                title: "Dispositivos",
                content: "La plataforma está optimizada para funcionar en computadoras de escritorio, laptops, tablets y smartphones. Sin embargo, para una experiencia completa, recomendamos usar un dispositivo con pantalla de al menos 10 pulgadas."
              },
              {
                title: "Conexión a Internet",
                content: "Se requiere una conexión a internet estable con velocidad mínima de 5 Mbps para un funcionamiento fluido, especialmente para las funcionalidades de videoconferencia y carga/descarga de documentos."
              },
              {
                title: "Almacenamiento y RAM",
                content: "La aplicación web no requiere instalación, pero recomendamos al menos 4GB de RAM para un rendimiento óptimo y suficiente espacio de almacenamiento para guardar documentos y registros médicos."
              }
            ],
            conclusion: "Si experimentas problemas técnicos, verifica primero que tu sistema cumpla con estos requisitos. Para asistencia adicional, contacta a nuestro equipo de soporte técnico disponible 24/7."
          }
        },
        {
          id: 'connection-troubleshooting',
          title: "Solución de problemas de conexión",
          icon: <AlertCircle size={24} />,
          content: {
            intro: "Si experimentas problemas de conexión con AGT Health, sigue estos pasos para resolverlos rápidamente.",
            sections: [
              {
                title: "Verificar Conexión a Internet",
                content: "Asegúrate de tener una conexión a internet estable. Puedes probar abriendo otros sitios web o utilizando herramientas como speedtest.net para verificar tu velocidad de conexión."
              },
              {
                title: "Reiniciar Navegador",
                content: "Cierra completamente tu navegador y vuelve a abrirlo. A veces, los problemas de conexión pueden resolverse simplemente reiniciando la aplicación del navegador."
              },
              {
                title: "Limpiar Caché",
                content: "Limpia el caché y las cookies de tu navegador. Esto puede resolver problemas relacionados con datos almacenados que podrían estar causando conflictos. Ve a la configuración de tu navegador, sección de privacidad o historial, y selecciona la opción para limpiar datos de navegación."
              },
              {
                title: "Verificar Firewall o VPN",
                content: "Si estás en una red corporativa, verifica que no haya restricciones de firewall que bloqueen el acceso a AGT Health. Si usas VPN, intenta desconectarla temporalmente para ver si esto resuelve el problema."
              },
              {
                title: "Probar en Modo Incógnito",
                content: "Abre una ventana de navegación privada o incógnito e intenta acceder a AGT Health. Esto puede ayudar a determinar si el problema está relacionado con extensiones del navegador o configuraciones específicas de tu cuenta."
              }
            ],
            conclusion: "Si después de seguir estos pasos sigues experimentando problemas, contacta a nuestro equipo de soporte técnico proporcionando detalles específicos sobre el error que estás enfrentando."
          }
        },
        {
          id: 'privacy-security',
          title: "Privacidad y seguridad de datos",
          icon: <Shield size={24} />,
          content: {
            intro: "En AGT Health, la seguridad y privacidad de los datos médicos es nuestra máxima prioridad. Conoce las medidas que implementamos para proteger la información sensible.",
            sections: [
              {
                title: "Encriptación de Datos",
                content: "Todos los datos transmitidos entre tu dispositivo y nuestros servidores están protegidos con encriptación SSL/TLS de 256 bits. Además, la información almacenada en nuestras bases de datos está encriptada con algoritmos avanzados."
              },
              {
                title: "Cumplimiento Normativo",
                content: "Nuestra plataforma cumple con las regulaciones internacionales de protección de datos de salud, incluyendo HIPAA (EE.UU.), GDPR (Europa) y leyes locales de protección de datos médicos. Regularmente realizamos auditorías de seguridad para mantener este cumplimiento."
              },
              {
                title: "Control de Acceso",
                content: "Implementamos un sistema de control de acceso basado en roles que garantiza que solo el personal autorizado pueda acceder a información específica. Cada acción en el sistema queda registrada para fines de auditoría."
              },
              {
                title: "Respaldo y Recuperación",
                content: "Realizamos copias de seguridad automáticas y periódicas de todos los datos para garantizar que la información médica nunca se pierda. Nuestros sistemas de recuperación permiten restaurar datos en caso de cualquier incidente."
              },
              {
                title: "Propiedad de los Datos",
                content: "Tú mantienes la propiedad completa de los datos de tus pacientes. AGT Health actúa únicamente como procesador de datos, y nunca compartimos, vendemos o utilizamos esta información para fines distintos a los expresamente autorizados por ti."
              }
            ],
            conclusion: "Si tienes preguntas específicas sobre nuestras políticas de privacidad y seguridad, no dudes en contactar a nuestro equipo de soporte o solicitar nuestra documentación detallada sobre protección de datos."
          }
        },
        {
          id: 'system-updates',
          title: "Actualizaciones del sistema",
          icon: <RefreshCw size={24} />,
          content: {
            intro: "Mantenemos AGT Health constantemente actualizado para mejorar la seguridad, rendimiento y añadir nuevas funcionalidades. Conoce cómo gestionamos las actualizaciones.",
            sections: [
              {
                title: "Frecuencia de Actualizaciones",
                content: "Realizamos actualizaciones menores de mantenimiento semanalmente, mientras que las actualizaciones mayores con nuevas funcionalidades se implementan mensualmente. Todas las actualizaciones están diseñadas para minimizar cualquier interrupción en tu trabajo."
              },
              {
                title: "Notificaciones",
                content: "Recibirás notificaciones sobre actualizaciones programadas con al menos 48 horas de anticipación para actualizaciones mayores. Las actualizaciones menores generalmente se realizan durante horas de baja actividad y no requieren acción de tu parte."
              },
              {
                title: "Cambios en la Interfaz",
                content: "Cuando implementamos cambios significativos en la interfaz de usuario, proporcionamos guías y tutoriales para ayudarte a adaptarte rápidamente. Nuestro objetivo es mejorar continuamente la experiencia de usuario sin crear confusión."
              },
              {
                title: "Compatibilidad",
                content: "Todas las actualizaciones son probadas exhaustivamente para garantizar compatibilidad con los navegadores y dispositivos más utilizados. Sin embargo, recomendamos mantener tu navegador actualizado para evitar problemas de compatibilidad."
              }
            ],
            conclusion: "Las actualizaciones son esenciales para mantener la plataforma segura y eficiente. Si experimentas algún problema después de una actualización, contacta inmediatamente a nuestro equipo de soporte."
          }
        },
        {
          id: 'performance-optimization',
          title: "Optimización de rendimiento",
          icon: <Zap size={24} />,
          content: {
            intro: "Maximiza el rendimiento de AGT Health en tu dispositivo con estos consejos de optimización.",
            sections: [
              {
                title: "Navegador Recomendado",
                content: "Para un rendimiento óptimo, recomendamos utilizar Google Chrome o Mozilla Firefox en sus versiones más recientes. Estos navegadores ofrecen el mejor soporte para las tecnologías web avanzadas que utilizamos."
              },
              {
                title: "Gestión de Pestañas",
                content: "Limita el número de pestañas abiertas en tu navegador mientras usas AGT Health. Demasiadas pestañas pueden consumir memoria RAM y ralentizar el rendimiento general de la aplicación."
              },
              {
                title: "Limpieza Regular",
                content: "Realiza limpiezas periódicas del caché y cookies de tu navegador. Esto ayuda a eliminar datos temporales que pueden acumularse y afectar el rendimiento con el tiempo."
              },
              {
                title: "Conexión a Internet",
                content: "Una conexión a internet estable y rápida es crucial. Si experimentas lentitud, verifica tu velocidad de internet y considera conectarte a una red más estable o utilizar una conexión por cable en lugar de WiFi si es posible."
              },
              {
                title: "Extensiones del Navegador",
                content: "Algunas extensiones del navegador pueden interferir con el funcionamiento de aplicaciones web. Si notas problemas de rendimiento, intenta desactivar temporalmente las extensiones o usar el modo incógnito para identificar si alguna extensión está causando conflictos."
              }
            ],
            conclusion: "Si después de aplicar estas recomendaciones sigues experimentando problemas de rendimiento, contacta a nuestro equipo de soporte técnico para una asistencia más personalizada."
          }
        }
      ]
    },
    {
      id: 'ai-features',
      icon: <Cpu size={24} />,
      title: "Funciones de IA",
      description: "Capacidades de inteligencia artificial",
      articles: [
        {
          id: 'ai-basics',
          title: "Conceptos básicos de IA",
          icon: <Book size={24} />,
          content: {
            intro: "La inteligencia artificial de AGT Health está diseñada para simplificar tu práctica profesional. Comprende los conceptos básicos para aprovechar al máximo esta herramienta.",
            sections: [
              {
                title: "¿Qué es AGT AI?",
                content: "AGT AI es un asistente virtual basado en inteligencia artificial avanzada, diseñado específicamente para profesionales de la salud. Está entrenado para entender terminología médica, gestionar agendas, procesar información de pacientes y automatizar tareas administrativas."
              },
              {
                title: "Interacción Natural",
                content: "Puedes comunicarte con AGT AI usando lenguaje natural, como si hablaras con un asistente humano. No necesitas aprender comandos especiales o sintaxis complicada. Simplemente escribe o habla de forma clara y específica sobre lo que necesitas."
              },
              {
                title: "Capacidades Principales",
                content: "AGT AI puede gestionar citas, buscar información de pacientes, crear registros, enviar recordatorios, analizar tendencias en datos médicos, convertir notas en tareas estructuradas y responder preguntas sobre el uso de la plataforma."
              },
              {
                title: "Aprendizaje Continuo",
                content: "El asistente aprende de tus interacciones para ofrecer un servicio cada vez más personalizado. Cuanto más lo utilices, mejor comprenderá tus preferencias y patrones de trabajo, adaptándose a tu estilo profesional."
              }
            ],
            conclusion: "AGT AI está diseñado para ser tu aliado en la gestión diaria de tu práctica profesional. Comienza con tareas simples e incrementa gradualmente la complejidad a medida que te familiarices con sus capacidades."
          }
        },
        {
          id: 'virtual-assistant',
          title: "Implementación de asistente virtual",
          icon: <MessageSquareText size={24} />,
          content: {
            intro: "Optimiza tu flujo de trabajo integrando el asistente virtual de AGT en tus rutinas diarias.",
            sections: [
              {
                title: "Acceso al Asistente",
                content: "El asistente virtual está disponible a través del chat de AGT, accesible desde cualquier pantalla de la plataforma. Puedes minimizarlo o maximizarlo según tus necesidades mientras trabajas en otras secciones."
              },
              {
                title: "Configuración Inicial",
                content: "La primera vez que interactúes con el asistente, te recomendamos presentarte y explicar brevemente tu especialidad y preferencias de trabajo. Esto ayudará a la IA a personalizar sus respuestas y sugerencias."
              },
              {
                title: "Comandos Frecuentes",
                content: "Algunos comandos útiles incluyen: 'Muéstrame mis citas de hoy', 'Busca al paciente [nombre]', 'Crea una nueva cita para [paciente] el [fecha] a las [hora]', 'Envía un recordatorio a todos mis pacientes de mañana', 'Resume los signos vitales de [paciente]'."
              },
              {
                title: "Integración con tu Agenda",
                content: "El asistente puede sincronizarse con tu calendario para sugerirte horarios disponibles cuando programes citas. También puede recordarte eventos importantes y ayudarte a reorganizar tu agenda en caso de cancelaciones."
              },
              {
                title: "Personalización Avanzada",
                content: "Puedes enseñar al asistente tus preferencias específicas, como la duración estándar de tus consultas, tus horarios preferidos, o frases personalizadas para comunicarse con los pacientes. Simplemente indícale tus preferencias y las recordará para futuras interacciones."
              }
            ],
            conclusion: "El asistente virtual está diseñado para adaptarse a tu estilo de trabajo. Dedica tiempo a familiarizarte con sus capacidades y a enseñarle tus preferencias para maximizar su utilidad."
          }
        },
        {
          id: 'auto-tasks',
          title: "Gestión de tareas automáticas",
          icon: <ListIcon size={24} />,
          content: {
            intro: "Automatiza tareas repetitivas para optimizar tu tiempo y enfocarte en lo que realmente importa: la atención a tus pacientes.",
            sections: [
              {
                title: "Conversión de Notas a Tareas",
                content: "Una de las funciones más útiles es AGT-Task. Escribe tus pendientes en el widget de Notas y luego presiona el botón 'Usar AGT-Task'. La IA analizará tu texto y creará automáticamente tareas estructuradas con fechas, prioridades y recordatorios."
              },
              {
                title: "Recordatorios Automáticos",
                content: "Configura el envío automático de recordatorios de citas a tus pacientes. Puedes personalizar el momento del envío (por ejemplo, 24 horas antes) y el contenido del mensaje. La plataforma se encargará de enviarlos vía WhatsApp sin intervención manual."
              },
              {
                title: "Seguimiento de Pacientes",
                content: "Programa seguimientos automáticos post-consulta. La IA puede enviar mensajes personalizados para verificar la evolución del paciente, recordarle tomar medicamentos o preguntarle sobre efectos secundarios, según tus indicaciones."
              },
              {
                title: "Informes Automáticos",
                content: "Configura la generación automática de informes periódicos sobre tu práctica: número de pacientes atendidos, tasa de asistencia a citas, promedios de signos vitales, o cualquier otro indicador relevante para tu especialidad."
              },
              {
                title: "Clasificación de Información",
                content: "La IA puede ayudarte a organizar automáticamente la información médica. Por ejemplo, puede categorizar resultados de laboratorio, identificar valores anormales o destacar tendencias preocupantes en los signos vitales de un paciente."
              }
            ],
            conclusion: "La automatización de tareas rutinarias puede ahorrarte horas de trabajo administrativo cada semana. Identifica procesos repetitivos en tu práctica y explora cómo la IA puede ayudarte a automatizarlos."
          }
        },
        {
          id: 'advanced-ai',
          title: "Funciones avanzadas de IA",
          icon: <Cpu size={24} />,
          content: {
            intro: "Descubre las capacidades más sofisticadas de la inteligencia artificial de AGT Health para llevar tu práctica profesional al siguiente nivel.",
            sections: [
              {
                title: "Análisis Predictivo",
                content: "La IA puede analizar los datos históricos de tus pacientes para identificar patrones y tendencias. Esto te permite anticipar cambios en su condición de salud y tomar medidas preventivas oportunas."
              },
              {
                title: "Asistencia en Diagnóstico",
                content: "Aunque no reemplaza tu juicio clínico, la IA puede sugerir posibles diagnósticos basados en los síntomas registrados, historial médico y resultados de laboratorio, funcionando como una herramienta de apoyo a la decisión."
              },
              {
                title: "Personalización de Tratamientos",
                content: "El sistema puede ayudarte a personalizar planes de tratamiento basados en las características específicas del paciente, su historial de respuesta a tratamientos previos y la evidencia médica más reciente."
              },
              {
                title: "Reconocimiento de Voz Médica",
                content: "Dicta notas clínicas directamente al asistente y observa cómo las transcribe con precisión, reconociendo terminología médica especializada y organizando la información en un formato estructurado."
              },
              {
                title: "Integración con Literatura Médica",
                content: "La IA puede buscar en bases de datos médicas para proporcionarte información actualizada sobre condiciones específicas, tratamientos innovadores o investigaciones recientes relevantes para tus casos."
              }
            ],
            conclusion: "Estas funciones avanzadas están diseñadas para complementar tu experiencia profesional, no para reemplazarla. Utilízalas como herramientas de apoyo que te permitan ofrecer una atención más precisa, personalizada y basada en evidencia."
          }
        },
        {
          id: 'patient-communication',
          title: "Comunicación con pacientes",
          icon: <Bell size={24} />,
          content: {
            intro: "La IA de AGT Health puede optimizar y personalizar la comunicación con tus pacientes, mejorando su experiencia y adherencia al tratamiento.",
            sections: [
              {
                title: "Recordatorios Personalizados",
                content: "Configura recordatorios automáticos que se adapten a cada paciente. Puedes personalizar el tono, contenido y frecuencia según las características del paciente, su condición médica o sus preferencias de comunicación."
              },
              {
                title: "Seguimiento Post-Consulta",
                content: "Programa mensajes de seguimiento automatizados para verificar la evolución del paciente después de la consulta. La IA puede enviar preguntas específicas y registrar las respuestas en el expediente del paciente."
              },
              {
                title: "Educación del Paciente",
                content: "Utiliza la IA para enviar información educativa personalizada a tus pacientes sobre su condición, tratamiento o hábitos saludables. Puedes programar estos envíos para que lleguen en momentos óptimos."
              },
              {
                title: "Respuestas a Consultas Frecuentes",
                content: "Configura respuestas automáticas para las preguntas más frecuentes de tus pacientes, como preparación para estudios, instrucciones post-procedimiento o información sobre medicamentos. Esto reduce la carga de comunicaciones rutinarias."
              },
              {
                title: "Encuestas de Satisfacción",
                content: "La IA puede enviar y procesar encuestas de satisfacción después de las consultas, proporcionándote retroalimentación valiosa para mejorar continuamente tu servicio."
              }
            ],
            conclusion: "Una comunicación efectiva con los pacientes mejora significativamente los resultados clínicos y la satisfacción. Aprovecha las capacidades de la IA para mantener un contacto constante y personalizado sin aumentar tu carga de trabajo."
          }
        }
      ]
    }
  ];

  useEffect(() => {
    // Verificar si Intercom está listo
    const checkIntercom = () => {
      if (typeof window !== 'undefined' && window.Intercom) {
        console.log('Intercom está disponible');
        setIntercomReady(true);
        setLoadingStatus('success');
        
        // Obtener mensajes no leídos
        try {
          const count = getUnreadCount();
          setUnreadCount(count);
          console.log('Mensajes no leídos:', count);
        } catch (error) {
          console.log('No se pudo obtener conteo de mensajes:', error);
        }
        
        // Verificar si el chat está abierto
        const intercomFrame = document.querySelector('.intercom-messenger-frame');
        setIsOpen(!!intercomFrame && 
                  window.getComputedStyle(intercomFrame).display !== 'none' &&
                  window.getComputedStyle(intercomFrame).visibility !== 'hidden');
                  
        return true;
      } else {
        console.log('Intercom aún no está disponible');
        return false;
      }
    };

    // Verificar inmediatamente
    checkIntercom();

    // Verificar cada segundo hasta que esté listo (máximo 20 segundos)
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      if (!intercomReady && attempts < maxAttempts) {
        const success = checkIntercom();
        attempts++;
        
        if (attempts >= maxAttempts && !success) {
          setLoadingStatus('error');
          console.error('No se pudo inicializar Intercom después de varios intentos');
        }
      } else {
        clearInterval(interval);
      }
    }, 1000);
    
    // Verificar periódicamente si el chat está abierto o cerrado
    const checkOpenInterval = setInterval(() => {
      if (intercomReady) {
        const intercomFrame = document.querySelector('.intercom-messenger-frame');
        setIsOpen(!!intercomFrame && 
                  window.getComputedStyle(intercomFrame).display !== 'none' &&
                  window.getComputedStyle(intercomFrame).visibility !== 'hidden');
      }
    }, 500);

    return () => {
      clearInterval(interval);
      clearInterval(checkOpenInterval);
    };
  }, [intercomReady, getUnreadCount]);

  const handleOpenChat = () => {
    console.log('Abriendo chat de soporte');
    showMessenger();
    setIsOpen(true);
  };
  
  const handleCloseChat = () => {
    console.log('Cerrando chat de soporte');
    hideMessenger();
    setIsOpen(false);
  };

  const handleStartNewConversation = () => {
    console.log('Iniciando nueva conversación');
    showNewMessage('Hola, necesito ayuda con AGT Health');
    setIsOpen(true);
  };

  const handleOpenArticle = (article) => {
    setSelectedArticle(article);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Función para obtener chip de estado
  const getStatusChip = () => {
    switch(loadingStatus) {
      case 'success':
        return (
          <StatusChip 
            statusType="connected" 
            icon={<CheckCircle size={16} />} 
            label={`Conectado ${unreadCount > 0 ? `(${unreadCount} mensajes)` : ''}`}
          />
        );
      case 'loading':
        return (
          <StatusChip 
            statusType="connecting" 
            icon={<Clock size={16} />} 
            label="Conectando..."
          />
        );
      case 'error':
        return (
          <StatusChip 
            statusType="error" 
            icon={<X size={16} />} 
            label="Error de conexión"
          />
        );
      default:
        return (
          <StatusChip 
            statusType="default" 
            label="Desconocido"
          />
        );
    }
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f5f5f7' }}>
      {/* Header */}
      <Container maxWidth="xl" sx={{ pt: 8, pb: 10 }}>
        <Box component={motion.div} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ mb: 4 }}
        >
          <Typography 
            variant="h1" 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3rem', lg: '5rem' }, 
              fontWeight: 600, 
              color: '#1d1d1f',
              mb: 1
            }}
          >
            Soporte
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              fontSize: { xs: '1.25rem', md: '1.5rem', lg: '1.75rem' }, 
              color: '#1d1d1f' 
            }}
          >
            ¿Cómo podemos ayudarte?
          </Typography>
        </Box>

        {/* Status Section */}
        <StyledPaper>
          <PaperHeader>
            <Typography variant="h6" fontWeight={600}>
              Estado del Soporte
            </Typography>
          </PaperHeader>
          
          <PaperContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="body1" fontWeight={500} color="text.secondary">
                Conexión
              </Typography>
              {getStatusChip()}
            </Box>
          </PaperContent>
        </StyledPaper>

        {/* Quick Links Section */}
        <Box sx={{ mb: 10 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: '1.75rem', md: '2rem', lg: '2.5rem' }, 
              fontWeight: 600, 
              color: '#1d1d1f',
              mb: 4
            }}
          >
            Soporte Rápido
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box 
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{
                  bgcolor: 'white',
                  borderRadius: 7,
                  p: 4,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', mb: 3 }}>
                  <Box sx={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '50%', 
                    bgcolor: '#f5f5f7', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0
                  }}>
                    <MessageCircle size={24} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Chat en Vivo
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Conéctate con nuestro equipo de soporte al instante
                    </Typography>
                    <Chip
                      label="Disponible"
                      size="small"
                      sx={{
                        mt: 1.5,
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        color: '#2e7d32',
                        fontWeight: 500,
                        borderRadius: '16px'
                      }}
                    />
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  {isOpen ? (
                    <OutlinedGreenButton
                      onClick={handleCloseChat}
                      variant="outlined"
                      fullWidth
                    >
                      Cerrar Chat
                    </OutlinedGreenButton>
                  ) : (
                    <GreenButton
                      onClick={handleOpenChat}
                      disabled={loadingStatus === 'error'}
                      fullWidth
                    >
                      Abrir Chat
                      {unreadCount > 0 && (
                        <Box 
                          component="span" 
                          sx={{ 
                            ml: 1, 
                            px: 1, 
                            py: 0.5, 
                            backgroundColor: 'error.main', 
                            borderRadius: '50%',
                            fontSize: '0.75rem',
                            minWidth: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {unreadCount}
                        </Box>
                      )}
                    </GreenButton>
                  )}
                  
                  <OutlinedGreenButton
                    onClick={handleStartNewConversation}
                    disabled={loadingStatus === 'error' || isOpen}
                    variant="outlined"
                    fullWidth
                  >
                    Nueva Consulta
                  </OutlinedGreenButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Browse Support Section */}
        <Box sx={{ mb: 10 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: '1.75rem', md: '2rem', lg: '2.5rem' }, 
              fontWeight: 600, 
              color: '#1d1d1f',
              mb: 4
            }}
          >
            Explorar Soporte
          </Typography>

          <Grid container spacing={4}>
            {supportCategories.map((category, index) => (
              <Grid item xs={12} md={4} key={category.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box
                    component={motion.button}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      bgcolor: 'white',
                      borderRadius: 7,
                      p: 4,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)'
                      }
                    }}
                  >
                    <IconCircle>
                      {category.icon}
                    </IconCircle>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {category.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ pr: 4 }}>
                      {category.description}
                    </Typography>
                    <ChevronRight 
                      style={{
                        position: 'absolute',
                        right: 32,
                        top: 32,
                        transform: activeCategory === category.id ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.3s ease'
                      }}
                      size={20}
                    />
                  </Box>

                  <AnimatePresence>
                    {activeCategory === category.id && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
                      >
                        {category.articles.map((article, idx) => (
                          <ArticleItem
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            onClick={() => handleOpenArticle(article)}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                '&:hover': { color: HEALTH_GREEN }
                              }}
                            >
                              <Typography variant="body1" sx={{ mr: 2 }}>
                                {article.title}
                              </Typography>
                              <ArrowRight size={16} />
                            </Box>
                          </ArticleItem>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQ Section */}
        <StyledPaper>
          <PaperHeader>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                Preguntas Frecuentes
              </Typography>
            </Box>
          </PaperHeader>
          
          <Box sx={{ p: 0 }}>
            {faqData.map((faq, index) => (
              <StyledAccordion key={faq.id} disableGutters>
                <StyledAccordionSummary
                  expandIcon={<ChevronDown />}
                  aria-controls={`faq-${faq.id}-content`}
                  id={`faq-${faq.id}-header`}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {faq.question}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </StyledAccordionDetails>
              </StyledAccordion>
            ))}
          </Box>
        </StyledPaper>

        {/* Support Center */}
        <StyledPaper>
          <PaperHeader>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Centro de Soporte AGT Health
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Asistencia personalizada para profesionales
                </Typography>
              </Box>
            </Box>
          </PaperHeader>
          
          <PaperContent>
            <Typography variant="body1" sx={{ mb: 4 }}>
              ¿Necesitas ayuda con AGT Health? Nuestro equipo de soporte está listo para asistirte 
              con cualquier consulta técnica o sobre el uso de la aplicación.
            </Typography>
            
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                <Typography 
                  variant="h3" 
                  fontWeight={600} 
                  color={HEALTH_GREEN}
                  sx={{ mb: 1 }}
                >
                  24/7
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Soporte disponible
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={20} color={HEALTH_GREEN} sx={{ mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1">
                      Soporte técnico especializado
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={20} color={HEALTH_GREEN} sx={{ mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1">
                      Resolución de problemas en tiempo real
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={20} color={HEALTH_GREEN} sx={{ mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1">
                      Asistencia con citas online y reuniones
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={20} color={HEALTH_GREEN} sx={{ mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1">
                      Capacitación personalizada para aprovechar las funcionalidades de IA
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={20} color={HEALTH_GREEN} sx={{ mr: 2, flexShrink: 0 }} />
                    <Typography variant="body1">
                      Ayuda con la integración de pacientes existentes y migración de datos
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </PaperContent>
        </StyledPaper>
      </Container>

      {/* Modal para artículos */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="article-modal-title"
        aria-describedby="article-modal-description"
      >
        <ModalContent>
          {selectedArticle && (
            <>
              <ModalHeader>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ModalIconCircle>
                    {selectedArticle.icon}
                  </ModalIconCircle>
                  <Box sx={{ ml: 2 }}>
                    <Typography id="article-modal-title" variant="h5" component="h2" fontWeight={600}>
                      {selectedArticle.title}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseModal} sx={{ p: 1 }}>
                  <X size={24} />
                </IconButton>
              </ModalHeader>

              <Divider sx={{ mb: 4 }} />

              <Box id="article-modal-description" sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {selectedArticle.content.intro}
                </Typography>

                {selectedArticle.content.sections.map((section, idx) => (
                  <Box key={idx} sx={{ mb: 4 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      {section.content}
                    </Typography>
                  </Box>
                ))}

                <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2 }}>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedArticle.content.conclusion}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <GreenButton onClick={handleCloseModal}>
                  Cerrar
                </GreenButton>
              </Box>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default IntercomDashboard;